import { ALL_CATALOGUE_ROLES } from "@repo/api-dtos"
import type { CatalogueLookupHit } from "@repo/game-admin"
import type { LookupHit } from "@repo/marketplace-admin"
import {
	type AdminIdentifier,
	classifyAdminIdentifier,
} from "@repo/shared-primitives"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { useDebounce } from "@/hooks/use-debounce"
import { useMarketplaceOrpc, useOrpc } from "@/integrations/orpc"

const CATALOGUE_KINDS = new Set(["hexId", "numeric", "code", "slug", "text"])

export interface EntityLookupResult {
	classified: AdminIdentifier
	marketplaceHits: LookupHit[]
	catalogueHits: CatalogueLookupHit[]
	isLoading: boolean
}

export function useEntityLookup(rawQuery: string): EntityLookupResult {
	const query = useDebounce(rawQuery.trim(), 150)
	const { hasAnyRole } = useAdminRolesContext()
	const { client: marketplaceClient, isAuthReady } = useMarketplaceOrpc()
	const { client: gameAdminClient } = useOrpc()

	const classified = useMemo(() => classifyAdminIdentifier(query), [query])

	const enabled =
		isAuthReady && query.length >= 2 && classified.kind !== "stripeOther"

	const marketplace = useQuery({
		queryKey: ["command-palette", "lookup", query],
		queryFn: () => marketplaceClient.lookup.resolve({ q: query, limit: 8 }),
		enabled,
		staleTime: 30_000,
		retry: false,
	})

	const catalogueEnabled =
		enabled &&
		hasAnyRole([...ALL_CATALOGUE_ROLES]) &&
		CATALOGUE_KINDS.has(classified.kind)

	const catalogue = useQuery({
		queryKey: ["command-palette", "catalogue-lookup", query],
		queryFn: () => gameAdminClient.staging.lookup({ q: query, limit: 8 }),
		enabled: catalogueEnabled,
		staleTime: 30_000,
		retry: false,
	})

	return {
		classified,
		marketplaceHits: marketplace.data?.hits ?? [],
		catalogueHits: catalogue.data?.hits ?? [],
		isLoading:
			(enabled && marketplace.isPending) ||
			(catalogueEnabled && catalogue.isPending),
	}
}
